import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, ReceiptIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextField } from '../../components/ui/Field';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { StatusTimeline } from '../../components/admin/StatusTimeline';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseStatuses, photoStages } from '../../types/admin';
import type { CaseStatus, PhotoStage } from '../../types/admin';
import { validateImage } from '../../services/imageService';
import { formatDate, formatDateTime, formatPrice, telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function CaseDetail() {
  const {
    caseId
  } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    customerById,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    updateCase,
    deleteCase,
    invoiceForCase,
    createInvoiceFromCase
  } = useAdminData();
  const item = cases.find(entry => entry.id === caseId);
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<PhotoStage>('Før');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  /** Valgte billeder der endnu ikke er uploadet — kan ses og fjernes først. */
  const [pending, setPending] = useState<{
    file: File;
    preview: string;
  }[]>([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  if (!item) return <Navigate to="/admin/sager" replace data-id="element-529" data-mp="3gg7k1iqbrbc8" />;
  const customer = customerById(item.customerId);
  const invoice = invoiceForCase(item.id);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-530" data-mp="3cgoxyjmrou7t">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-531" data-mp="3jiu7nakdv01m">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-532" data-mp="3tgi362ptcbku" />
        Sager
      </Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-533" data-mp="3tenhgm6pwffp">
        <div data-id="element-534" data-mp="235c2ftdl9f9">
          <p className="text-sm text-ink-muted" data-id="element-535" data-mp="23nfpxidapx7">Sag #{item.number}</p>
          <h1 className="mt-1 font-display text-[clamp(1.7rem,4.5vw,2.4rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-536" data-mp="62w7iwtukdq4">
            {item.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-537" data-mp="g1c0748ivv1q">
            {customer?.name ?? item.customerName}
            {item.fromWebsite && <span className="ml-2 rounded-full bg-canvas-deep px-2 py-0.5 text-xs text-ink-muted" data-id="element-538" data-mp="kzwz90c1g8ol">
                Fra hjemmesiden
              </span>}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3" data-id="element-539" data-mp="2msbic2qbjj6l">
          <StatusBadge status={item.status} data-id="element-540" data-mp="13x6yo3bzzbc2" />
          <button type="button" onClick={() => {
          if (window.confirm(`Slet sag #${item.number}? Handlingen kan ikke fortrydes.`)) {
            deleteCase(item.id);
            navigate('/admin/sager');
          }
        }} className="inline-flex h-9 items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-541" data-mp="13y8bodckq7wf">
            <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-542" data-mp="1iupkawtdp8fa" />
            Slet sag
          </button>
        </div>
      </header>

      {item.quoteId && <section className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-543" data-mp="18wjl4myyige4">
          <p className="text-[15px] text-ink" data-id="element-544" data-mp="2hiwl2c9h0hk">
            Oprettet fra tilbud: <span className="font-medium" data-id="element-545" data-mp="3tlzgzwd18vam">{item.quoteNumber}</span>
          </p>
          <Link to={`/admin/tilbud/${item.quoteId}`} className={buttonClasses('secondary', 'sm')} data-id="element-546" data-mp="1ldt0jxj95wdt">
            Åbn tilbud
          </Link>
        </section>}

      {/* Faktura: oprettes fra sagen, typisk når arbejdet er færdigt. */}
      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-547" data-mp="1ce5w12v88agj">
        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-548" data-mp="1hd0rp5ealti2">
          <div data-id="element-549" data-mp="2510gw0vyhbuj">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-550" data-mp="251a4jhtj36x2">
              {invoice ? 'Faktura allerede oprettet' : 'Faktura'}
            </h2>
            <p className="mt-1 text-[15px] text-ink-soft" data-id="element-551" data-mp="1v3ckl6ipbwa9">
              {invoice ? `${invoice.invoiceNumber} · ${formatPrice(String(Math.round(invoice.total)))} · ${invoice.status}` : item.status === 'Færdig' ? 'Arbejdet er færdigt. Opret fakturaen ud fra sagen — alt kan rettes bagefter.' : 'Du kan oprette en fakturakladde, når du er klar. Alt kopieret kan rettes bagefter.'}
            </p>
          </div>
          {invoice ? <Link to={`/admin/fakturaer/${invoice.id}`} className={buttonClasses('secondary', 'md')} data-id="element-552" data-mp="c4y6k3qi7oyy">
              <ReceiptIcon className="h-4 w-4" aria-hidden="true" data-id="element-553" data-mp="q3616502ie6y" />
              Åbn faktura
            </Link> : <Button size="md" disabled={creatingInvoice} onClick={async () => {
          if (creatingInvoice) return;
          setCreatingInvoice(true);
          setInvoiceError('');
          const created = await createInvoiceFromCase(item);
          setCreatingInvoice(false);
          if (!created) {
            setInvoiceError('Fakturaen kunne ikke oprettes. Prøv igen.');
            return;
          }
          navigate(`/admin/fakturaer/${created.id}`);
        }} data-id="element-554" data-mp="l39svtkc80ll">
              {creatingInvoice ? <>
                  <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-555" data-mp="19rfcekzw8x48" />
                  Opretter …
                </> : 'Opret faktura'}
            </Button>}
        </div>
        {invoiceError && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-556" data-mp="1oh3fnrrmy5ul">
            {invoiceError}
          </p>}
      </section>

      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-557" data-mp="qumhkkb2vo7s">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-558" data-mp="vrw51uewppsz">
          <SelectField id="sag-status" label="Status" className="w-full sm:w-64" value={item.status} onChange={event => setStatus(item.id, event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-559" data-mp="10rsa7wzcbna4" />
        </div>
        <div className="mt-6" data-id="element-560" data-mp="2g77tt8osxwnd">
          <StatusTimeline status={item.status} data-id="element-561" data-mp="2galveepn61tr" />
        </div>
      </section>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-562" data-mp="4knxdpt0wcfe">
        <div className="space-y-5" data-id="element-563" data-mp="jc336z7f30v">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-564" data-mp="kdp26hrd9xd">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-565" data-mp="3rwymio45sofb">Opgave</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-566" data-mp="3my3qull3f5ds">
              {item.description || 'Ingen beskrivelse endnu.'}
            </p>
            <dl className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-3" data-id="element-567" data-mp="3d0z4olr8z9wz">
              <div data-id="element-568" data-mp="3d3bwdqmt4yn8">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-569" data-mp="38z44xloyt4lb">Kategori</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-570" data-mp="340aszxpbtln9">{item.category}</dd>
              </div>
              <div data-id="element-571" data-mp="1780rkonoxbx2">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-572" data-mp="178kbgawwiadq">Adresse</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-573" data-mp="128egakrfa5qw">{item.address || '—'}</dd>
              </div>
              <div data-id="element-574" data-mp="b323dx3v62kc">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-575" data-mp="b4mxjlm9njho">Planlagt dato</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-576" data-mp="g1n4dyqxwq9u">{formatDate(item.plannedDate)}</dd>
              </div>
            </dl>
            <TextField id="sag-dato" label="Ret planlagt dato" type="date" className="mt-5 max-w-xs" value={item.plannedDate} onChange={event => updateCase(item.id, {
            plannedDate: event.target.value
          })} data-id="element-577" data-mp="1dijqp8bu4of" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-578" data-mp="3shqs1tksuy7c">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-579" data-mp="3em52nlanmcee">Noter</h2>
            <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            if (!note.trim()) return;
            addNote(item.id, note.trim());
            setNote('');
          }} data-id="element-580" data-mp="3jjnfvnvpfds7">
              <label htmlFor="ny-note" className="sr-only" data-id="element-581" data-mp="32acg2w7dhkl2">
                Ny note
              </label>
              <input id="ny-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." className="flex-1 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-faint transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none" data-id="element-582" data-mp="2x8vib9k6qe8p" />
              <Button type="submit" variant="dark" data-id="element-583" data-mp="3uz08rcugbc8n">
                Tilføj note
              </Button>
            </form>
            <ul className="mt-5 space-y-3" data-id="element-584" data-mp="3q0ylsgtwmsrw">
              {item.internalNotes.map(entry => <motion.li key={entry.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              ease
            }} className="flex items-start justify-between gap-3 rounded-xl bg-canvas p-4" data-id="element-585" data-mp="20lijnleee7bi">
                  <div data-id="element-586" data-mp="2h2cffe71ejpw">
                    <p className="text-xs text-ink-muted" data-id="element-587" data-mp="2c0ws1fhe014c">{formatDateTime(entry.createdAt)}</p>
                    <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-588" data-mp="2723gvtpkom7c">{entry.text}</p>
                  </div>
                  <button type="button" onClick={() => deleteNote(item.id, entry.id)} aria-label="Slet note" className="shrink-0 rounded-full p-1.5 text-ink-faint transition-colors duration-200 ease-smooth hover:bg-white hover:text-ink" data-id="element-589" data-mp="34qdfrzu2atu8">
                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-590" data-mp="2q0el471pu0ji" />
                  </button>
                </motion.li>)}
              {item.internalNotes.length === 0 && <li className="text-sm text-ink-muted" data-id="element-591" data-mp="3io23dffikf2z">Ingen noter endnu.</li>}
            </ul>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-592" data-mp="3r0lwl0ro3b1a">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-593" data-mp="3v04hhwjhnm44">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-594" data-mp="3siapv1h33j1">Billeder</h2>
              <div className="flex items-center gap-2" data-id="element-595" data-mp="8to4lw6vcwu5">
                <div className="flex gap-1.5" role="group" aria-label="Vælg kategori for upload" data-id="element-596" data-mp="drpj03tco7za">
                  {photoStages.map(option => <button key={option} type="button" onClick={() => setStage(option)} aria-pressed={stage === option} className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ease-smooth ${stage === option ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-597" data-mp="hrzz49auwerv">
                      {option}
                    </button>)}
                </div>
                <label htmlFor="sag-billeder" className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink transition-colors duration-200 ease-smooth hover:border-ink/30" data-id="element-598" data-mp="ljhioeojl6gr">
                  {uploading ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-599" data-mp="plcljnm4frwn" /> : <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-600" data-mp="14gipm51npgnc" />}
                  {uploading ? 'Uploader …' : 'Vælg billeder'}
                  <input id="sag-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={event => {
                  const input = event.target;
                  const files = Array.from(input.files ?? []);
                  input.value = '';
                  const problems = files.map(validateImage).filter(Boolean) as string[];
                  const accepted = files.filter(file => !validateImage(file));
                  setUploadError(problems.join(' '));
                  // Billederne lægges i kø, så de kan ses og fjernes før upload.
                  setPending(current => [...current, ...accepted.map(file => ({
                    file,
                    preview: URL.createObjectURL(file)
                  }))]);
                }} data-id="element-601" data-mp="3696xirp8ws9g" />
                </label>
              </div>
            </div>

            {pending.length > 0 && <div className="mt-5 rounded-xl border border-line bg-canvas p-4" data-id="element-602" data-mp="2nf6v6zujs36g">
                <p className="text-sm text-ink-soft" data-id="element-603" data-mp="neieyzm3rfkb">
                  {pending.length} {pending.length === 1 ? 'billede' : 'billeder'} klar til {stage}
                </p>
                <ul className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-5" data-id="element-604" data-mp="edkc4knehe3b">
                  {pending.map((entry, index) => <li key={`${entry.file.name}-${index}`} className="relative" data-id="element-605" data-mp="9fafs6pxw7g4" data-mpi={index}>
                      <img src={entry.preview} alt={entry.file.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-606" data-mp="4fxrrinc0uby" />
                      <button type="button" onClick={() => {
                  URL.revokeObjectURL(entry.preview);
                  setPending(current => current.filter((_, i) => i !== index));
                }} aria-label={`Fjern ${entry.file.name}`} className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-607" data-mp="3qw0ln3q52gj5">
                        <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-608" data-mp="22athxevjmkav" />
                      </button>
                    </li>)}
                </ul>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-609" data-mp="26alsty8et2je">
                  <Button size="sm" disabled={uploading} onClick={async () => {
                if (uploading) return;
                setUploading(true);
                setUploadError('');
                const result = await addPhotos(item.id, pending.map(entry => entry.file), stage);
                setUploading(false);
                if (!result.ok) {
                  // Køen bevares, så uploaden kan forsøges igen.
                  setUploadError(result.error ?? 'Billederne kunne ikke uploades.');
                  return;
                }
                pending.forEach(entry => URL.revokeObjectURL(entry.preview));
                setPending([]);
              }} data-id="element-610" data-mp="19nywaemzpevt">
                    {uploading ? <>
                        <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-611" data-mp="1fam5jyt0ot0g" />
                        Uploader …
                      </> : uploadError ? 'Prøv upload igen' : `Upload til ${stage}`}
                  </Button>
                  <button type="button" disabled={uploading} onClick={() => {
                pending.forEach(entry => URL.revokeObjectURL(entry.preview));
                setPending([]);
                setUploadError('');
              }} className="inline-flex h-9 items-center rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink disabled:opacity-50" data-id="element-612" data-mp="2zpo4pqls4upo">
                    Ryd
                  </button>
                </div>
              </div>}

            {uploadError && <p role="alert" className="mt-4 text-sm font-medium text-danger" data-id="element-613" data-mp="3qx6h6oxdajt0">
                {uploadError}
              </p>}

            <div className="mt-5 space-y-6" data-id="element-614" data-mp="3c78r23fvnk16">
              {photoStages.map(group => {
              const photos = item.photos.filter(photo => photo.stage === group);
              return <div key={group} data-id="element-615" data-mp="8qrthy9aqvhf">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-616" data-mp="8pgt88f285v2">{group}</h3>
                    {photos.length > 0 ? <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-617" data-mp="29mg7v1gzhj8x">
                        {photos.map(photo => <li key={photo.id} className="relative" data-id="element-618" data-mp="24tenidy0crhy">
                            <img src={photo.url} alt={`${group} — ${photo.name}`} className="aspect-square w-full rounded-xl object-cover" data-id="element-619" data-mp="20u5uxcepeznz" />
                            <button type="button" onClick={() => deletePhoto(item.id, photo.id)} aria-label={`Slet billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-620" data-mp="2oip37rxr7cg2">
                              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-621" data-mp="134195mfdupud" />
                            </button>
                          </li>)}
                      </ul> : <p className="mt-2 text-sm text-ink-muted" data-id="element-622" data-mp="xy5scp5f5mdf">Ingen billeder.</p>}
                  </div>;
            })}
            </div>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-623" data-mp="1brx29yn2b89o">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-624" data-mp="1bqvk7a7mplig">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-625" data-mp="1gqgq64l7uu4o">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-626" data-mp="1lpjjwsvqxdxc">
              {customer?.name ?? item.customerName}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-627" data-mp="1qn3ams9mrj66">
              <li data-id="element-628" data-mp="3nir9cv52pbwy">{customer?.phone || '—'}</li>
              <li className="break-all" data-id="element-629" data-mp="3sgj5k1cr3k6y">{customer?.email || '—'}</li>
              <li data-id="element-630" data-mp="3sgsurmpz1nbl">{customer?.address || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-631" data-mp="20g854r71w25h">
              <LinkButton href={telHref(customer?.phone ?? '')} variant="secondary" size="sm" data-id="element-632" data-mp="24gikgugqg4x0">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-633" data-mp="29fv476cb4vvd" />
                Ring
              </LinkButton>
              <LinkButton href={`mailto:${customer?.email ?? ''}`} variant="secondary" size="sm" data-id="element-634" data-mp="2e74vschf299w">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-635" data-mp="2j67qp43omz45" />
                Send e-mail
              </LinkButton>
            </div>
            {customer && <Link to={`/admin/kunder/${customer.id}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-636" data-mp="2o6lycl1dg71n">
                Se kundeprofil
              </Link>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-637" data-mp="xtwktthnxj3y">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-638" data-mp="12shhxk1mbmo6">Økonomi</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-639" data-mp="v8p2p8gqkq1g">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-640" data-mp="qgowwzdzj2wy">
                <dt className="text-ink-soft" data-id="element-641" data-mp="qfdsqygmnt5b">Timepris</dt>
                <dd className="font-medium text-ink" data-id="element-642" data-mp="livri8kiacxq">{formatPrice(item.hourlyRate, 'kr./time')}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-643" data-mp="gku97puwmhnb">
                <dt className="text-ink-soft" data-id="element-644" data-mp="1e719woo37pue">Fast tilbud</dt>
                <dd className="font-medium text-ink" data-id="element-645" data-mp="197orqh85v6y5">{formatPrice(item.fixedPrice)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-646" data-mp="148lngz0094r8">
                <dt className="text-ink-soft" data-id="element-647" data-mp="146seve5u0agl">Anslået beløb</dt>
                <dd className="font-medium text-ink" data-id="element-648" data-mp="105yp0f8twll2">{formatPrice(item.estimate)}</dd>
              </div>
            </dl>
            <TextField id="sag-slutbeloeb" label="Endeligt beløb (kr.)" className="mt-4" inputMode="numeric" placeholder="Udfyldes når opgaven er færdig" value={item.finalAmount} onChange={event => updateCase(item.id, {
            finalAmount: event.target.value
          })} data-id="element-649" data-mp="3p0dx09ct37uz" />
          </section>
        </aside>
      </div>
    </div>;
}